import { weekFileName } from '../lib/weekFileName';
import { DocLangToggle } from './ui/doc-lang-toggle';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import {
  Plus, ClipboardCheck, CheckCircle, Zap, Wrench, FileText, FileSignature, MapPin, Copy,
  Download, Eye, Edit, Printer, Camera, Trash2, CloudOff
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import ChecklistFillForm from './iso9001/ChecklistFillForm';
import LazyAuthImage from './shared/LazyAuthImage';
import { loadSession, patchSession } from '../lib/sessionState';
import { SearchSortBar, CREATED_SORT_OPTIONS, matchesQuery, matchesWeek, byCreated, byText } from './ListSearchSort';
import { useSavedView } from '../hooks/useSavedView';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import useDeepShortcut from '../hooks/useDeepShortcut';
import usePermissions from '../hooks/usePermissions';
import { DateText } from './ui/week-badge';
import {
  cacheChecklist, getCachedChecklist, listCachedChecklists, isLocalId,
  LOCAL_ID_PREFIX, cacheTemplates, getCachedTemplates,
} from '../lib/offlineQueue';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  draft: { label: i18n.t('auto.lbl.draft'), color: 'bg-gray-100 text-gray-700' },
  in_progress: { label: i18n.t('auto.lbl.inProgress'), color: 'bg-blue-100 text-blue-700' },
  completed: { label: i18n.t('auto.lbl.approved'), color: 'bg-green-100 text-green-700' },
  conditional_approval: { label: i18n.t('auto.lbl.conditional'), color: 'bg-orange-100 text-orange-700' },
  signed_off: { label: i18n.t('auto.lbl.signedOff'), color: 'bg-green-100 text-green-700' },
};

const ProjectChecklists = ({ projectId, projectName, focusInstanceId }) => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { token, user: currentUser } = useAuth();
  const [instances, setInstances] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [viewInstance, setViewInstance] = useState(null);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterLocation, setFilterLocation] = useState('');
  const { search, setSearch, sortBy, setSortBy } = useSavedView('project-checklists');
  const [week, setWeek] = useState(null);
  const isOnline = useOnlineStatus();

  // Arriving from a machine card: open that checklist straight away so the
  // engineer can start ticking instead of hunting for it in the list.
  const focusedRef = useRef(null);
  useEffect(() => {
    if (!focusInstanceId || focusedRef.current === focusInstanceId) return;
    focusedRef.current = focusInstanceId;
    handleOpenInstance(focusInstanceId);
  }, [focusInstanceId]);

  // Dashboard shortcuts: pin the open sheet, or the New sheet picker.
  useDeepShortcut({
    active: !!viewInstance,
    tab: 'projects',
    context: { openProjectId: projectId, projectTab: 'checklists', focusChecklistId: viewInstance?.id },
    label: viewInstance?.checklist_number || viewInstance?.template_name,
    subtitle: projectName || '',
    icon: 'checklists',
    perms: ['projects.view'],
    priority: 3,
    focusKey: 'focusChecklistId',
    onFocus: (id) => handleOpenInstance(id),
  });
  useDeepShortcut({
    active: showCreate,
    tab: 'projects',
    context: { openProjectId: projectId, projectTab: 'checklists', create: 'checklist' },
    label: t('shortcuts.qc.checklist'),
    subtitle: projectName || '',
    icon: 'checklists',
    perms: ['projects.view'],
    priority: 4,
    focusKey: 'create',
    onFocus: (v) => { if (v === 'checklist') setShowCreate(true); },
  });

  // Coming back after the tab was killed (phone call, low memory): reopen the
  // sheet that was on screen rather than dropping the user in the list.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const last = loadSession()?.openSheet;
    if (last?.source === 'project' && last.projectId === projectId && last.id) {
      handleOpenInstance(last.id);
    }
    /* eslint-disable-next-line */
  }, [projectId]);

  // Keep the session breadcrumb pointing at the open sheet, so a reload
  // (or a phone call that kills the tab) lands back inside it.
  useEffect(() => {
    patchSession({ openSheet: viewInstance ? { source: 'project', id: viewInstance.id, projectId } : null });
  }, [viewInstance, projectId]);

  // When the queue drains, the server has the real numbers — reload.
  useEffect(() => {
    const onChanged = () => { fetchInstances(); };
    window.addEventListener('offline-queue-changed', onChanged);
    return () => window.removeEventListener('offline-queue-changed', onChanged);
    /* eslint-disable-next-line */
  }, [projectId]);

  // Header-data lookup that tolerates template-specific key names
  // (templates generate keys from labels, e.g. system__equipment_ref).
  const headerVal = (h, patterns) => {
    if (!h) return '';
    for (const [k, v] of Object.entries(h)) {
      if (!v) continue;
      if (patterns.some(p => p.test(k))) return String(v);
    }
    return '';
  };
  const SYSTEM_KEYS = [/^system/, /system.*(ref|equip)/, /equipment.*ref/];
  const PANEL_KEYS = [/^panel/, /panel.*(ref|install)/, /installation.*ref/];

  // Pull location/system-ref out of an instance's header_data, used both
  // for the filter dropdown options and the per-row label.
  const locationOf = (inst) => {
    const h = inst.header_data || {};
    return h.location || h.installation_site || h.system_ref || h.equipment_ref || '';
  };
  const locationOptions = useMemo(() => {
    const set = new Set();
    for (const i of instances) {
      const l = locationOf(i);
      if (l) set.add(l);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [instances]);
  const CHECKLIST_SORTS = {
    created_desc: byCreated('desc'),
    created_asc: byCreated('asc'),
    number: byText((i) => i.checklist_number),
    type: byText((i) => i.template_name),
    status: byText((i) => i.status),
    owner: byText((i) => i.created_by_name),
  };
  const filteredInstances = useMemo(() => {
    const fl = filterLocation.toLowerCase();
    const list = instances.filter((i) => {
      if (filterLocation && locationOf(i).toLowerCase() !== fl) return false;
      if (!matchesWeek(week, i.created_at)) return false;
      const h = i.header_data || {};
      return matchesQuery(search, [
        i.checklist_number, i.doc_ref, i.template_name, i.discipline,
        i.status, i.created_by_name,
        h.project_name, h.client, h.machine_id__asset_no,
        headerVal(h, SYSTEM_KEYS), headerVal(h, PANEL_KEYS),
        h.location, h.installation_site,
      ]);
    });
    return [...list].sort(CHECKLIST_SORTS[sortBy] || CHECKLIST_SORTS.created_desc);
    /* eslint-disable-next-line */
  }, [instances, filterLocation, search, sortBy, week]);

  const fetchInstances = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/checklists/instances?project_id=${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('fetch failed');
      const server = await resp.json();
      // Checklists started offline live only on the device until they
      // sync, so merge them in (and drop any the server now knows about).
      const cached = await listCachedChecklists(projectId).catch(() => []);
      const localOnly = cached
        .filter((c) => isLocalId(c.id))
        .map((c) => ({ ...c.instance, _offline: true }));
      setInstances([...localOnly, ...server]);
    } catch {
      // No signal — fall back entirely to what's on the device.
      const cached = await listCachedChecklists(projectId).catch(() => []);
      setInstances(cached.map((c) => ({ ...c.instance, _offline: isLocalId(c.id), _cached: true })));
    }
  }, [projectId, token]);

  const fetchTemplates = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/checklists/templates`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('fetch failed');
      const data = await resp.json();
      setTemplates(data);
      cacheTemplates(data);
    } catch {
      setTemplates(getCachedTemplates());
    }
  }, [token]);

  // Fetch projects and employees for the fill form
  const fetchSupport = useCallback(async () => {
    try {
      const [projResp, empResp] = await Promise.all([
        fetch(`${API_BASE}/api/projects`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE}/api/employees`, { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);
      if (projResp.ok) setProjects(await projResp.json());
      if (empResp.ok) setEmployees(await empResp.json());
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchInstances();
    fetchTemplates();
  }, [fetchInstances, fetchTemplates]);

  const handleCreate = async () => {
    if (!selectedTemplateId) { toast.error(t('auto.misc.selectATemplate')); return; }
    setIsCreating(true);
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        await createOffline();
        return;
      }
      const resp = await fetch(`${API_BASE}/api/checklists/instances`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ template_id: selectedTemplateId, project_id: projectId }),
      });
      if (!resp.ok) throw new Error((await resp.json()).detail || 'Failed');
      const data = await resp.json();
      toast.success(t('auto.misc.checklistCreatedAndLinkedToProject'));
      setShowCreate(false);
      setSelectedTemplateId('');
      fetchInstances();
      // Open the new checklist
      handleOpenInstance(data.id);
    } catch (e) { toast.error(e.message); }
    finally { setIsCreating(false); }
  };

  /**
   * Start a checklist with no signal. It gets a temporary number so the
   * engineer can see which sheet is which; the server issues the real
   * CL-xxxx when the queue drains.
   */
  const createOffline = async () => {
    const template = templates.find((t) => t.id === selectedTemplateId)
      || getCachedTemplates().find((t) => t.id === selectedTemplateId);
    if (!template) {
      toast.error(t('auto.misc.thatTemplateIsNotAvailableOffline'));
      return;
    }
    const localId = `${LOCAL_ID_PREFIX}${crypto.randomUUID()}`;
    const instance = {
      id: localId,
      checklist_number: 'CL-PENDING',
      template_id: template.id,
      template_name: template.name,
      doc_ref: template.doc_ref,
      discipline: template.discipline,
      project_id: projectId,
      status: 'draft',
      header_data: {},
      sections_data: (template.sections || []).map((s) => ({
        items: (s.items || []).map(() => ({ status: '', value: '', notes: '' })),
      })),
      sign_offs: (template.sign_off_roles || []).map((r) => ({ ...r, signed: false })),
      created_at: new Date().toISOString(),
      created_by_name: currentUser?.full_name || currentUser?.username || '',
    };
    await cacheChecklist(instance, template);
    toast.success('Started on this device — it will upload when you\'re back online');
    setShowCreate(false);
    setSelectedTemplateId('');
    await fetchInstances();
    setViewInstance({ ...instance, _template: template, _offline: true });
  };

  const handleOpenInstance = async (instId) => {
    // Fetch support data if not loaded
    if (projects.length === 0) await fetchSupport();
    const openFromDevice = async () => {
      const cached = await getCachedChecklist(instId).catch(() => null);
      if (!cached) {
        toast.error(t('auto.misc.thisChecklistIsNotOnYour'));
        return false;
      }
      setViewInstance({ ...cached.instance, _template: cached.template, _cached: true });
      return true;
    };

    if (isLocalId(instId) || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      await openFromDevice();
      return;
    }
    try {
      const resp = await fetch(`${API_BASE}/api/checklists/instances/${instId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to load');
      const inst = await resp.json();
      const tplResp = await fetch(`${API_BASE}/api/checklists/templates/${inst.template_id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (tplResp.ok) inst._template = await tplResp.json();
      setViewInstance(inst);
    } catch (e) {
      if (await openFromDevice()) return;
      toast.error(e.message);
    }
  };

  // "Copy & edit" — server-side clone (carries over template, project,
  // header_data; resets results, sign-offs, status to draft, generates
  // a new auto number). Immediately opens the new draft so the user
  // can tweak location / equipment ref and start filling.
  const handleCopyInstance = async (instId) => {
    try {
      const resp = await fetch(`${API_BASE}/api/checklists/instances/${instId}/copy`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Copy failed');
      }
      const data = await resp.json();
      toast.success(`Copied as ${data.checklist_number}`);
      await fetchInstances();
      handleOpenInstance(data.id);
    } catch (e) { toast.error(e.message); }
  };

  const handleExportPDF = async (instId, monoOrEvent = false, withPhotos = false) => {
    // Accept either (instId, mono:bool, withPhotos:bool) or (instId, event) for backward compatibility
    let mono = false;
    if (monoOrEvent && typeof monoOrEvent === 'object' && 'stopPropagation' in monoOrEvent) {
      monoOrEvent.stopPropagation();
    } else {
      mono = !!monoOrEvent;
    }
    toast.info(withPhotos ? 'Generating PDF with photos…' : (mono ? t('auto.misc.generatingBWPdf') : t('auto.misc.generatingPdf')));
    try {
      const params = new URLSearchParams();
      if (mono) params.append('mono', 'true');
      if (withPhotos) params.append('with_photos', 'true');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const resp = await fetch(`${API_BASE}/api/checklists/instances/${instId}/pdf${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('PDF generation failed');
      const blob = await resp.blob();
      const disposition = resp.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="(.+)"/);
      const filename = match ? match[1] : `checklist_${instId}${mono ? '_BW' : ''}${withPhotos ? '_with_photos' : ''}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = weekFileName(filename);
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('auto.misc.pdfDownloaded'));
    } catch (e) { toast.error(e.message); }
  };

  const getProgress = (inst) => {
    if (!inst.sections_data) return 0;
    let total = 0, filled = 0;
    for (const sec of inst.sections_data) {
      for (const item of (sec.items || [])) {
        total++;
        if (item.status || item.value || item.notes) filled++;
      }
    }
    return total ? Math.round((filled / total) * 100) : 0;
  };

  // Who may change a sheet is decided by the permissions matrix. Someone
  // with Checklists · Edit can work on any sheet; without it they can still
  // finish a sheet they started themselves.
  const canEdit = (inst) => {
    if (!currentUser) return false;
    return can('iso_checklists.edit') || currentUser.id === inst.created_by;
  };

  // Deleting needs Checklists · Delete. Signed-off sheets are never deleted
  // from the project view — they're archived from the ISO 9001 page so the
  // audit trail survives.
  const canDelete = (inst) => {
    if (!currentUser) return false;
    if (inst.status === 'completed' || inst.status === 'signed_off') return false;
    return can('iso_checklists.delete');
  };

  const handleDelete = async (inst) => {
    if (!window.confirm(
      `Delete checklist ${inst.checklist_number || ''}?\n\n` +
      `${inst.template_name || 'This checklist'} and all of its recorded data will be permanently removed. This cannot be undone.`
    )) return;
    try {
      const resp = await fetch(`${API_BASE}/api/checklists/instances/${inst.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Delete failed');
      }
      toast.success(t('auto.misc.checklistDeleted'));
      await fetchInstances();
    } catch (e) { toast.error(e.message); }
  };

  // Full-screen dialog for viewing/editing a checklist
  if (viewInstance) {
    return (
      <>
        <div className="space-y-3" data-testid="project-checklists">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" />
              Linked Checklists ({instances.length})
            </h3>
          </div>
        </div>
        <Dialog open={!!viewInstance} onOpenChange={(open) => { if (!open) { setViewInstance(null); fetchInstances(); } }}>
          <DialogContent className="top-0 left-0 right-0 translate-x-0 translate-y-0 h-[100dvh] w-screen max-w-none rounded-none content-start sm:content-normal p-3 overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)] sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:h-auto sm:max-h-[92vh] sm:w-[95vw] sm:max-w-[95vw] sm:rounded-lg sm:p-6">
            {/* The form renders its own heading; this satisfies Radix's
                screen-reader requirement without duplicating it visually. */}
            <DialogTitle className="sr-only">
              {viewInstance?.checklist_number} — {viewInstance?.template_name}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t('auto.misc.fillInAndSignOffThis')}
            </DialogDescription>
            <ChecklistFillForm
              instance={viewInstance}
              template={viewInstance._template}
              token={token}
              projects={projects}
              employees={employees}
              currentUser={currentUser}
              onBack={() => { setViewInstance(null); fetchInstances(); }}
              onExportPDF={(mono) => handleExportPDF(viewInstance.id, !!mono)}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="space-y-3" data-testid="project-checklists">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4" />
          Linked Checklists ({filteredInstances.length}{(filterLocation || search) ? ` / ${instances.length}` : ''})
          {!isOnline && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600"
              data-testid="checklists-offline-notice"
            >
              <CloudOff className="h-3 w-3" />{t('auto.misc.offlineShowingSheetsOnThisDevice')}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {(locationOptions.length > 0 || filterLocation) && (
            <div className="flex items-center gap-1 min-w-0">
              <Label className="text-xs text-gray-500 flex items-center gap-1 shrink-0"><MapPin className="h-3 w-3" />{t('auto.misc.location')}</Label>
              <Select value={filterLocation || '__all__'} onValueChange={(v) => setFilterLocation(v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-8 w-36 sm:w-44" data-testid="checklist-location-filter"><SelectValue placeholder={t('auto.misc.all')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('auto.misc.allLocations')}</SelectItem>
                  {locationOptions.map((loc) => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button size="sm" variant="outline" onClick={() => setShowCreate(true)} data-testid="project-new-checklist">
            <Plus className="h-3.5 w-3.5 mr-1" /> {t('auto.misc.newChecklist')}
          </Button>
        </div>
      </div>

      <SearchSortBar
        search={search}
        onSearch={setSearch}
        sortBy={sortBy}
        onSort={setSortBy}
        placeholder={t('auto.misc.searchByNumberTypeSystemPanel')}
        testIdPrefix="project-checklists"
        week={week}
        onWeek={setWeek}
        options={[
          ...CREATED_SORT_OPTIONS,
          { value: 'number', label: t('auto.lbl.checklistNumber') },
          { value: 'type', label: t('auto.lbl.checklistType') },
          { value: 'status', label: t('auto.lbl.status') },
          { value: 'owner', label: t('auto.lbl.owner') },
        ]}
      />

      {filteredInstances.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-4">
          {instances.length === 0 ? t('auto.misc.noChecklistsLinkedToThisProject') : t('auto.misc.noChecklistsMatchTheCurrentSearch')}
        </p>
      ) : (
        <div className="space-y-2">
          {filteredInstances.map(inst => {
            const statusCfg = STATUS_CONFIG[inst.status] || STATUS_CONFIG.draft;
            const DIcon = inst.discipline === 'electrical' ? Zap : inst.discipline === 'mechanical' ? Wrench : FileText;
            const progress = getProgress(inst);
            const signedCount = inst.sign_offs?.filter(s => s.signed).length || 0;
            const totalSignOffs = inst.sign_offs?.length || 0;

            const hdr = inst.header_data || {};
            const loc = hdr.location || hdr.installation_site || '';
            const sysRef = headerVal(hdr, SYSTEM_KEYS);
            const panelRef = headerVal(hdr, PANEL_KEYS);
            const machineRef = hdr.machine_id__asset_no || '';
            const inspDate = hdr.inspection_date;
            const isLocked = inst.status === 'signed_off';
            return (
              <div
                key={inst.id}
                className="flex flex-col sm:flex-row sm:items-start gap-1.5 sm:gap-3 p-2.5 border rounded-lg hover:bg-gray-50 hover:border-gray-300 group cursor-pointer transition-colors"
                onClick={() => handleOpenInstance(inst.id)}
                data-testid={`checklist-card-${inst.id}`}
              >
                <div className="flex items-center gap-2 shrink-0">
                  <DIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  {inst.asset_photo_id && (
                    <LazyAuthImage
                      src={`${API_BASE}/api/checklists/asset-photo/${inst.asset_id}`}
                      alt={inst.asset_label || 'equipment'}
                      className="block h-12 w-12 shrink-0 overflow-hidden rounded border bg-gray-50"
                      testId={`pc-asset-thumb-${inst.id}`}
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {inst.checklist_number && <span className="font-mono text-xs font-semibold">{inst.checklist_number}</span>}
                    {inst.doc_ref && <span className="font-mono text-[10px] text-gray-400 uppercase">{inst.doc_ref}</span>}
                    <span className="text-sm font-medium break-words">{inst.template_name}</span>
                    {inst.discipline && (
                      <Badge variant="outline" className="text-[10px] capitalize">{inst.discipline}</Badge>
                    )}
                    <Badge className={`${statusCfg.color} text-xs`}>{statusCfg.label}</Badge>
                    {inst._offline && (
                      <Badge
                        variant="outline"
                        className="text-[10px] bg-slate-100 text-slate-600 border-slate-300 gap-1"
                        data-testid={`checklist-pending-sync-${inst.id}`}
                      >
                        <CloudOff className="h-3 w-3" />{t('auto.misc.notUploadedYet')}
                      </Badge>
                    )}
                    {isLocked && (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{t('auto.misc.locked')}</Badge>
                    )}
                  </div>
                  {(inst.template_description || inst.asset_label) && (
                    <div className="mt-0.5 text-xs text-gray-500" data-testid={`pc-description-${inst.id}`}>
                      {[inst.template_description, inst.asset_label, inst.asset_make_model].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {(loc || sysRef || panelRef || machineRef) && (
                    <div className="mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs">
                      {loc && (
                        <span className="flex items-center gap-1 text-[#AA0000] font-medium" data-testid={`pc-location-${inst.id}`}>
                          <MapPin className="h-3 w-3" />{loc}
                        </span>
                      )}
                      {sysRef && (
                        <span className="text-gray-500" data-testid={`pc-system-${inst.id}`}>
                          System / Equipment: <span className="font-medium text-gray-700">{sysRef}</span>
                        </span>
                      )}
                      {panelRef && (
                        <span className="text-gray-500" data-testid={`pc-panel-${inst.id}`}>
                          Panel / Installation: <span className="font-medium text-gray-700">{panelRef}</span>
                        </span>
                      )}
                      {machineRef && (
                        <span className="text-gray-500" data-testid={`pc-machine-${inst.id}`}>
                          Machine: <span className="font-medium text-gray-700">{machineRef}</span>
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-1 flex-wrap text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <div className="w-20 h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${progress === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${progress}%` }} />
                      </div>
                      <span className="text-gray-400">{progress}%</span>
                    </div>
                    {totalSignOffs > 0 && (
                      <span className="flex items-center gap-1">
                        <FileSignature className="h-3 w-3" />
                        {signedCount === totalSignOffs ? (
                          <span className="text-green-600">{t('auto.misc.signed')}</span>
                        ) : (
                          <span>{signedCount}/{totalSignOffs}</span>
                        )}
                      </span>
                    )}
                    {inspDate && (
                      <span title={t('auto.misc.inspectionDate')}><span className="text-gray-400">{t('auto.misc.inspected')}</span> <DateText value={inspDate} /></span>
                    )}
                    {!inspDate && inst.created_at && (
                      <span title={t('auto.misc.created')}><span className="text-gray-400">{t('auto.misc.created')}</span> <DateText value={inst.created_at} /></span>
                    )}
                    {inst.created_by_name && inst.created_by_name !== '—' && (
                      <span title={t('auto.misc.owner')}><span className="text-gray-400">by</span> {inst.created_by_name}</span>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-1 border-t pt-1 opacity-100 transition-opacity flex-shrink-0 sm:border-0 sm:pt-0 sm:opacity-0 sm:group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleOpenInstance(inst.id); }} title={canEdit(inst) ? "Edit" : "View"} data-testid={`view-checklist-${inst.id}`}>
                    {canEdit(inst) ? <Edit className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleCopyInstance(inst.id); }} title="Copy as new draft" data-testid={`copy-checklist-${inst.id}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()} title="Export PDF" data-testid={`export-pdf-${inst.id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <div className="px-2 py-1"><DocLangToggle testId={`doc-lang-projectchecklists-0-${inst.id}`} /></div>
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); handleExportPDF(inst.id, false, false); }}
                        data-testid={`export-pdf-color-${inst.id}`}
                      >
                        <Download className="h-4 w-4 mr-2" /> {t('auto.misc.colorPdf')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); handleExportPDF(inst.id, true, false); }}
                        data-testid={`export-pdf-bw-${inst.id}`}
                      >
                        <Printer className="h-4 w-4 mr-2" /> Print-friendly (B&amp;W)
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); handleExportPDF(inst.id, false, true); }}
                        data-testid={`export-pdf-color-with-photos-${inst.id}`}
                      >
                        <Camera className="h-4 w-4 mr-2" /> {t('auto.misc.colorPhotoEvidence')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); handleExportPDF(inst.id, true, true); }}
                        data-testid={`export-pdf-bw-with-photos-${inst.id}`}
                      >
                        <Camera className="h-4 w-4 mr-2" /> B&amp;W + Photo Evidence
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {canDelete(inst) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleDelete(inst); }}
                      title={t('auto.misc.deleteChecklist')}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-checklist-${inst.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Quick Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Checklist for {projectName}</DialogTitle>
            <DialogDescription>{t('auto.misc.selectATemplateProjectDataWill')}</DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <SelectTrigger><SelectValue placeholder={t('auto.misc.selectTemplate')} /></SelectTrigger>
              <SelectContent>
                {templates.map(tpl => (
                  <SelectItem key={tpl.id} value={tpl.id}>
                    <span className="flex items-center gap-2">
                      {tpl.discipline === 'electrical' ? <Zap className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}
                      {tpl.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} data-testid="dialog-cancel-btn">{t('auto.misc.cancel')}</Button>
            <Button onClick={handleCreate} disabled={isCreating || !selectedTemplateId} data-testid="dialog-create-btn">
              {isCreating ? t('auto.misc.creating') : t('auto.misc.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectChecklists;
